import { useState } from 'preact/hooks';

interface InquiryFormProps {
  siteKey: string;
  conversationId?: string;
  apiBaseUrl: string;
  themeColor: string;
  onSubmitted: () => void;
  onClose?: () => void;
}

export default function InquiryForm({
  siteKey,
  conversationId,
  apiBaseUrl,
  themeColor,
  onSubmitted,
  onClose
}: InquiryFormProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setError('');

    if (!name.trim() || !email.trim() || !content.trim()) {
      setError('名前、メールアドレス、問い合わせ内容は必須です。');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('有効なメールアドレスを入力してください。');
      return;
    }

    setSubmitting(true);

    try {
      const res = await fetch(`${apiBaseUrl}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteKey,
          conversationId: conversationId || undefined,
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim() || undefined,
          content: content.trim()
        })
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        throw new Error(data.error || '送信に失敗しました');
      }

      setSubmitted(true);
      onSubmitted();
    } catch (err: any) {
      setError(err.message || '送信中にエラーが発生しました。');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div style={{
        padding: '20px',
        textAlign: 'center',
        background: '#f0fdf4',
        borderRadius: '12px',
        margin: '8px',
        border: '1px solid #bbf7d0'
      }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }}>✅</div>
        <p style={{ fontWeight: '600', color: '#166534', fontSize: '14px', marginBottom: '4px' }}>
          送信ありがとうございました
        </p>
        <p style={{ color: '#15803d', fontSize: '12px' }}>
          担当者より回答いたします。しばらくお待ちください。
        </p>
      </div>
    );
  }

  return (
    <div style={{
      padding: '12px',
      margin: '8px',
      background: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    }}>
      <p style={{
        fontWeight: '600',
        fontSize: '14px',
        color: '#374151',
        marginBottom: '12px'
      }}>
        📩 お問い合わせフォーム
      </p>

      {error && (
        <div style={{
          padding: '8px 10px',
          background: '#fef2f2',
          borderRadius: '6px',
          marginBottom: '10px',
          fontSize: '12px',
          color: '#dc2626'
        }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '10px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
            名前 <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="text"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            placeholder="例: 山田太郎"
            required
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '10px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
            メールアドレス <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <input
            type="email"
            value={email}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
            placeholder="example@email.com"
            required
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '10px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
            電話番号
          </label>
          <input
            type="tel"
            value={phone}
            onInput={(e) => setPhone((e.target as HTMLInputElement).value)}
            placeholder="090-1234-5678"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              outline: 'none',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
            問い合わせ内容 <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <textarea
            value={content}
            onInput={(e) => setContent((e.target as HTMLTextAreaElement).value)}
            placeholder="お問い合わせ内容をお書きください..."
            required
            rows={3}
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
              outline: 'none',
              resize: 'vertical',
              boxSizing: 'border-box',
              fontFamily: 'inherit'
            }}
          />
        </div>

        <button
          type="submit"
          disabled={submitting}
          style={{
            width: '100%',
            padding: '10px',
            background: submitting ? '#9ca3af' : themeColor,
            color: '#ffffff',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: submitting ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s'
          }}
        >
          {submitting ? '送信中...' : '送信する'}
        </button>
      </form>

      {onClose && (
        <button
          onClick={onClose}
          style={{
            width: '100%',
            marginTop: '8px',
            padding: '6px',
            background: 'transparent',
            color: '#6b7280',
            border: 'none',
            fontSize: '12px',
            cursor: 'pointer'
          }}
        >
          閉じる
        </button>
      )}
    </div>
  );
}
